import { utils, dates } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { ContextData, ContextFilterItem, MultiContextData, PeriodDimensionType, ReportContext } from "@reporting/rp-types/analytics";
import { DimensionDefinition, IndicatorDefinition } from "@reporting/rp-types/definition";
import { PeriodParsingOptions, parsePeriod } from "./model";
import { cloneContext } from "./model/cloneContext";

const MODULE_PERIODS: Partial<Record<ModuleCode, string[]>> = {
	'F': ['PM', 'PX', 'PC'],
	'I': ['PM', 'PW', 'PX', 'PC'],
	'A': ['PM', 'PW', 'PX', 'PC'],
	'D': ['PM', 'PW', 'PX'],
	'S': ['PM', 'PW', 'PX'],
	'C': ['PM', 'PW', 'PX'],
}

const PERIOD_LABELS: Record<string, string> = {'PM': 'months', 'PW': 'weeks', 'PX': 'dynamic', 'PC': 'common'};

// the descriptions must be built at call time - at module import the locale
// catalog is not loaded yet and msg() would bake in the English sources
export const getPeriodDimensions = (module: ModuleCode = 'I') => MODULE_PERIODS[module].map(p => ({
	dim_num: p,
	description: `${msg('Period')} (${msg(PERIOD_LABELS[p])})`,
} as DimensionDefinition));

// [
// 	{dim_num: 'PM', description: `${msg('Period')} (${msg('months')})`},
// 	{dim_num: 'PW', description: `${msg('Period')} (${msg('weeks')})`},
// 	{dim_num: 'PX', description: `${msg('Period')} (${msg('dynamic')})`},
// 	{dim_num: 'PC', description: `${msg('Period')} (${msg('common')})`},
// ];

export const getIndicatorDimensions = (indicators: IndicatorDefinition[]): DimensionDefinition[] => [
	{dim_num: 'I', description: msg('Indicators')}
];

export const getUnitDimensions = (): DimensionDefinition[] => [
	{dim_num: 'U', description: msg('Units')}
];

export const getScenarioDimensions = (): DimensionDefinition[] => [
	{dim_num: 'AB', description: msg('Scenario')}
];

export const getModuleSpecificDimensions = (module: ModuleCode) : DimensionDefinition[] => {
	switch (module) {
		case 'F': return [
			...getScenarioDimensions(),
		];
		default: return [];
	}
}

export const isPeriodDimension = (dim: string) => dim.charAt(0).toUpperCase() == 'P';
export const isIndicatorDimension = (dim: string) => dim.toUpperCase() == 'I';
export const isUnitDimension = (dim: string) => dim.toUpperCase() == 'U';
export const isRowsDimension = (dim: string) => dim.toUpperCase() == 'R';
export const isScenariosDimension = (dim: string) => dim.toUpperCase() == 'AB';
export const isInvoiceDimension = (dim: string) => dim.toUpperCase() == 'IR';
export const isStandardDimension = (dim: string) => !isNaN(((dim ?? '').split(':')[0]) as undefined as number);
export const isStandardDimensionAttribute = (dim: string) => (dim ?? '').includes(':');

export const isCustomViewType = (view: string) => /^custom\-/.test(view);

export const isDataModule = (module: ModuleCode) => ['F', 'I', 'A'].includes(module); // data module = Financial, Items, Aging

export type DimensionType = 'P'|'D'|'I'|'U'|'R'|'AB'|'IR'; // Period, Dimension, Indicator, Unit, Rows, Scenarios, Invoice (AP/AR)

export const getIndicatorKey = (params: {id?: number|string, id_indicator?: number|string, type?: string, balance?: boolean}): string => {
	const parts: string[] = [params.type];
	if (params.balance) parts.push('BALANCE');
	const id = params.id ?? params.id_indicator;
	if (id != null) parts.push(id.toString());
	return parts.join('_').toUpperCase();
}

export const parseIndicatorKey = (key: string) => {
	const parts = key.toUpperCase().split('_');
	const type = parts[0];
	const balance = parts[1] === 'BALANCE';
	const id = parts[balance ? 2 : 1];
	return {id, type, balance};
}

export const getDimensionType = (dim: DimensionType) =>
	isStandardDimension(dim) ? 'D' :
	isPeriodDimension(dim) ? 'P' :
	isIndicatorDimension(dim) ? 'I' :
	isUnitDimension(dim) ? 'U' :
	isRowsDimension(dim) ? 'R' :
	isScenariosDimension(dim) ? 'AB' :
	isInvoiceDimension(dim) ? 'IR' :
	null;

export const isExtensionContext = (context: ReportContext | ContextData | string) => {
	let view: string = '';
	let module: string;
	if (typeof context === 'string') view = context;
	else if ((context as ReportContext).context) {
		view = ((context as ReportContext).context as ContextData).view;
		module = (context as ReportContext).module;
	} else if ((context as ContextData).view) {
		view = (context as ContextData).view;
		module = (context as ContextData).module;
	}

	return module == 'C' || /^custom\-/.test(view);
}

export const isMultiContext = (context: ReportContext|ContextData|MultiContextData|string) =>
	['S', 'D'].includes(typeof context === 'string' ? context : context?.module);

export const isTransactionContext = (context: ReportContext|ContextData|MultiContextData) => {
	if ((context as ReportContext).context) {
		return ((context as ReportContext).context as ContextData).view == 'transactions';
	} else if ((context as ContextData).view) {
		return (context as ContextData).view == 'transactions';
	}
	return false;
}

export * from './model/itemUid';
export * from './model/cloneContext';
export * from './model/contextFilters';

export const anonimizeContext = (context: ReportContext): ReportContext => {
	const anonContext = cloneContext(context);
	delete anonContext.id_context;
	delete anonContext.idz;
	delete anonContext.hash;
	return anonContext;
}

export const findColumnIndex = (columns: Record<string, any>[], name: string) =>
	columns.findIndex(col => col.name.toUpperCase() == (name || '').toString().toUpperCase());

export const getContextFilterIds = (items: ContextFilterItem[] | string[] | string) => {
	if (typeof items === 'string') return [items];
	return items?.map(item => Array.isArray(item) ? item[0] : item) ?? [];
}

export const getContextYears = (context: ContextData, options?: PeriodParsingOptions) => {
	const ids: [PeriodDimensionType, string[]][] = context.filters
		.filter(d => isPeriodDimension(d.dim))
		.map(d => [d.dim as PeriodDimensionType, getContextFilterIds(d.items)]);
	const years = new Set<number>();
	ids.forEach(item => item[1].forEach(id => {
		const period = parsePeriod(id, item[0]);
		if (period?.start?.date) years.add(dates.getFiscalYear(period.start.date, options.monthOffset));
		if (period?.end?.date) years.add(dates.getFiscalYear(period.end.date, options.monthOffset));
	}));
	return Array.from(years).sort();
}

export const getModuleKey = (module: ModuleCode) : string => {
	switch (module) {
		case 'F': return 'finance';
		case 'I': return 'items';
		case 'A': return 'aging';
		case 'D': return 'dashboard';
		case 'C': return 'custom';
		case 'S': return 'spreadsheet';
		default: return 'unknown';
	}
}
